import { ReactElement, useCallback, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Chip,
  CircularProgress,
  debounce,
  Grid,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AppFormikSelectWithKeys,
  AppFormikTextField,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { ReactComponent as AIIcon } from 'assets/images/mcp-title-icon.svg';
import { ReactComponent as NoServerFound } from 'assets/images/no-servers-foundsvg.svg';
import AddIcon from '@mui/icons-material/Add';
import { Formik } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { MCPServersService } from 'services/MCPServersService';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getStatusColors } from './records-server/Main';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import useSize from 'shared/hooks/useSize';

const MCPServer = (): ReactElement => {
  const { isMobile } = useSize();
  const dispatch = useDispatch();
  const mcpServerPagination = useSelector(
    (state: IAppState) => state.paginationState.mcpServer
  );

  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [mcpServersData, setMCPServersData] = useState<any[]>([]);

  const navigate = useNavigate();
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  // API Calls
  const loadMCPServers = (): void => {
    setIsLoadingRecords(true);
    const queryString = new URLSearchParams({
      offset: String(mcpServerPagination.offset),
      limit: String(mcpServerPagination.pageLimit),
      serverName: mcpServerPagination.search,
      status: mcpServerPagination.status,
    }).toString();
    MCPServersService.getMCPServers(queryString)
      .then((response) => {
        const { servers, pagination } = response.data.data;
        setMCPServersData(servers);

        dispatch(
          PaginationActions.setPagination({
            mcpServer: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  const debouncSearch = useCallback(
    debounce((searchValue: any) => {
      dispatch(
        PaginationActions.setPagination({
          mcpServer: {
            ...mcpServerPagination,
            offset: 1,
            search: searchValue,
          },
        })
      );
    }, 500),
    [mcpServerPagination]
  );

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        mcpServer: {
          offset: page + 1,
        },
      })
    );
  };

  const handleStatusChange = (statusValue: string): void => {
    dispatch(
      PaginationActions.setPagination({
        mcpServer: {
          ...mcpServerPagination,
          offset: 1,
          status: statusValue,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        mcpServer: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  const handleClearFilter = (): void => {
    dispatch(
      PaginationActions.setPagination({
        mcpServer: {
          offset: 1,
          search: '',
          status: '',
        },
      })
    );
  };

  useEffect(() => {
    loadMCPServers();
  }, [
    mcpServerPagination.search,
    mcpServerPagination.pageLimit,
    mcpServerPagination.offset,
    mcpServerPagination.status,
  ]);

  return (
    <>
      <PageHeader
        title="MCP Server Registry"
        description={`Govern every MCP server connecting to your AI tools — ${
          mcpServersData?.length || 0
        } registered.`}
        actions={
          <>
            {' '}
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.mcpServers.registerNewMCPServer'
            ) && (
              <AppPrimaryButton
                className="btn-small"
                onClick={() => navigate('new')}
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
                title="Register MCP Server"
              />
            )}
          </>
        }
        customIcon={<AIIcon />}
      />

      <Box className="risk-management risk-assessment">
        <HeadTag title="MCP Servers" />

        <Box className="risk-assessments-wrapper">
          <Formik
            initialValues={{
              'search-server': mcpServerPagination.search,
              status: mcpServerPagination.status,
            }}
            onSubmit={() => {
              console.log('search');
            }}
          >
            {({ resetForm }) => (
              <>
                <Stack
                  className="risk-management-nav-wrapper"
                  direction={isMobile ? 'column' : 'row'}
                >
                  <Box
                    width={isMobile ? '100%' : '60%'}
                    className="risk-management-search"
                  >
                    <AppFormikTextField
                      inputProps={{
                        startAdornment: (
                          <SvgIcon
                            component={Search}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        ),
                      }}
                      outerLabel={false}
                      placeholder="Search Servers ..."
                      name="search-server"
                      onChange={(e: any) => {
                        debouncSearch(e.target.value);
                      }}
                    />
                  </Box>
                  <Grid container className="risk-assessment-nav">
                    <Grid item lg={3} sm={4} xs={12}>
                      <AppFormikSelectWithKeys
                        name="status"
                        className="rework-selection "
                        notched={false}
                        labelId="fix-selection-label"
                        testid="solicitor-dropdown"
                        MenuProps={{
                          className: 'menu-fix-selection single-fix-rework',
                        }}
                        outerLabel
                        placeholder="Filter Status"
                        notEmphasized
                        notSelectablePlaceholder={false}
                        label=""
                        titleKey="title"
                        valueKey="value"
                        items={[
                          { title: 'All Statuses', value: '' },
                          { title: 'Proposed', value: 'proposed' },
                          { title: 'Pilot', value: 'pilot' },
                          { title: 'Approved', value: 'approved' },
                          { title: 'Restricted', value: 'restricted' },
                          { title: 'Retired', value: 'retired' },
                        ]}
                        onChange={(e: any) => {
                          handleStatusChange(e.target.value);
                        }}
                      />
                    </Grid>
                    <Grid item lg={3} sm={4} xs={12}>
                      <AppPrimaryButtonOutlined
                        fullWidth
                        title="Clear Filters"
                        onClick={() => {
                          resetForm();
                          handleClearFilter();
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>

                {isLoadingRecords && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}
                {!isLoadingRecords && (
                  <TableContainer>
                    <Table
                      className="app-table"
                      sx={{ minWidth: 650 }}
                      aria-label="simple table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Server name</TableCell>
                          <TableCell>Registry ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Environment</TableCell>
                          <TableCell>Hosting</TableCell>
                          <TableCell>Approval</TableCell>
                          <TableCell>Last review</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mcpServersData.length > 0 ? (
                          mcpServersData.map((row: any) => (
                            <TableRow
                              key={row._id}
                              sx={{
                                '& .MuiTableCell-root': {
                                  whiteSpace: 'nowrap',
                                },
                                '&:last-child td, &:last-child th': {
                                  border: 0,
                                },
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                if (
                                  orgACLConfig.aiInventory?.includes(
                                    'aiInventory.mcpServers.view'
                                  )
                                ) {
                                  navigate(`records/${row._id}`);
                                }
                              }}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                                className="word-break"
                              >
                                {row.server_name}
                              </TableCell>
                              <TableCell>MCP-{row._id.slice(-8)}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  sx={{
                                    background: getStatusColors(row.status)
                                      ?.background,
                                    color: getStatusColors(row.status)?.color,
                                    textTransform: 'capitalize',
                                  }}
                                  label={row.status ?? 'Draft'}
                                />
                              </TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>
                                {row.environment_scope}
                              </TableCell>
                              <TableCell sx={{ textTransform: 'capitalize' }}>
                                {row.hosting_model}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  sx={{
                                    background: getStatusColors(
                                      row.approval_status
                                    )?.background,
                                    color: getStatusColors(row.approval_status)
                                      ?.color,
                                  }}
                                  label={row.approval_status ?? 'Draft'}
                                />
                              </TableCell>
                              <TableCell>
                                {dayjs(row.updatedAt ?? dayjs()).format(
                                  'DD/MM/YYYY'
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <EmptyPlaceholder
                                title={
                                  <Typography
                                    sx={{
                                      fontSize: 15,
                                      fontWeight: 600,
                                      color: '#06362b',
                                    }}
                                  >
                                    No MCP servers registered yet
                                  </Typography>
                                }
                                message={
                                  <>
                                    <Typography mb={3}>
                                      Register your first MCP server to start
                                      governing tool access.
                                    </Typography>
                                    <AppPrimaryButtonOutlined
                                      className="btn-small"
                                      onClick={() => navigate('new')}
                                      startIcon={<AddIcon />}
                                      title="Register MCP Server"
                                    />
                                  </>
                                }
                                icon={
                                  <SvgIcon
                                    component={NoServerFound}
                                    inheritViewBox
                                    className="view-icon"
                                    htmlColor="#76A81F"
                                    sx={{ fontSize: 70 }}
                                  />
                                }
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Formik>
        </Box>
        {!isLoadingRecords && mcpServersData.length > 0 && (
          <TablePagination
            component="div"
            count={mcpServerPagination.totalRecords}
            page={mcpServerPagination.offset - 1}
            rowsPerPage={mcpServerPagination.pageLimit}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
            disabled={isLoadingRecords}
          />
        )}
      </Box>
    </>
  );
};

export default MCPServer;
